import { EmptyState } from '../components/EmptyState.js';
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { useBrand } from '../lib/brand-context.js';
import { useAuth } from '../lib/auth-context.js';
import { apiGet, apiFetch } from '../lib/api.js';
import { useToast } from '../hooks/useToast.js';
import { runAioBackfill } from '../api/aioCitations.js';
import { runGeminiSourceDomainRepair } from '../api/geminiSourceDomains.js';
import { runAioSearchStatusRepair } from '../api/aioSearchStatus.js';
import type { AioSearchStatusProgress } from '../api/aioSearchStatus.js';
import { runDomainClassificationRepair } from '../api/domainClassification.js';
import type { DomainClassificationProgress } from '../api/domainClassification.js';
import type { GeminiSourceDomainProgress } from '../api/geminiSourceDomains.js';
import { ProcessingStatus } from '../components/ProcessingStatus.js';
import type { DomainListResponse, DomainListItem } from '../types/index.js';
import { DomainTypeBadge } from '../components/DomainTypeBadge.js';
import { TypeDistributionChart } from '../components/TypeDistributionChart.js';
import { SourceTrendChart } from '../components/SourceTrendChart.js';
import { Pagination } from '../components/Pagination.js';
import { Icon } from '../components/ui/Icon.js';
import { Select } from '../components/Select.js';
import { TableSkeleton } from '../components/Skeleton.js';
import { Button } from '../components/ui/Button.js';
import { OverflowMenu } from '../components/ui/OverflowMenu.js';
import { Tabs, tabPanelId } from '../components/Tabs.js';
import type { TabItem } from '../components/Tabs.js';
import { useFullBleed } from '../lib/layout-width.js';
import { StageLoop } from '../components/how-it-works/diagrams/LoopDiagram.js';

const PAGE_SIZE = 20;
const ENGINE_OPTIONS = ['chatgpt', 'gemini', 'claude', 'google_aio', 'mistral'] as const;
// Two vocabularies, not one.
//
// FILTER_TYPE_OPTIONS — everything a source can be SHOWN as. "Your domains" and
// "Competitor" are worked out for the brand you are looking at, so they are
// perfectly good things to filter by.
//
// SETTABLE_TYPE_OPTIONS — everything a source can be SET to by hand. This list
// covers every brand in the account, and whether a domain is *yours* depends on
// which brand is asking, so those two are recognised from the brand's own
// registered domain and its competitor list instead of typed in here. Offering
// them would be offering a choice that gets overridden the moment anyone views
// a brand.
const FILTER_TYPE_OPTIONS = ['you', 'competitor', 'institutional', 'editorial', 'corporate', 'reference', 'ugc', 'other'] as const;
const SETTABLE_TYPE_OPTIONS = ['institutional', 'editorial', 'corporate', 'reference', 'ugc', 'other'] as const;

function TrendArrow({ value }: { value: number | null }) {
  if (value === null || value === undefined) {
    return <Icon name="bx-minus" className="text-gray-300" />;
  }
  if (value > 0) return <Icon name="bx-trending-up" className="text-green-600" />;
  if (value < 0) return <Icon name="bx-trending-down" className="text-red-600" />;
  return <Icon name="bx-minus" className="text-gray-400" />;
}

function ReliabilityLabel({ sampleSize }: { sampleSize: number | null }) {
  if (sampleSize === null) return <span className="text-xs text-gray-400">--</span>;
  let tier = 'directional';
  let color = 'text-amber-600';
  if (sampleSize >= 400) { tier = 'high confidence'; color = 'text-green-600'; }
  else if (sampleSize >= 50) { tier = 'operational'; color = 'text-blue-600'; }
  return (
    <span className={`text-xs ${color}`} title={`${tier}: ${sampleSize} samples`}>
      {sampleSize} <span className="text-gray-400">({tier})</span>
    </span>
  );
}

function formatRate(rate: number | null): string {
  if (rate === null) return '--';
  return `${(rate * 100).toFixed(1)}%`;
}

const DOMAIN_VIEWS: readonly TabItem<'domains' | 'gap'>[] = [
  { id: 'domains', label: 'Domains', testId: 'view-domains' },
  { id: 'gap', label: 'Gap analysis', testId: 'view-gap-analysis' },
];

export function SourcesPage() {
  useFullBleed();
  const { selectedBrand, user } = useBrand();
  const { hasRole } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  // Spec 127 — admin-only source-repair triggers (AIO citation backfill, and
  // spec 196's Gemini source-domain repair). DOM-absent for non-admins
  // (mirrors the spec 099/100 role-gate pattern). Both controls share one gate.
  const canRunSourceRepairs = hasRole('rule_admin') || hasRole('orbit:admin');
  const [aioModalOpen, setAioModalOpen] = useState(false);
  const [aioSubmitting, setAioSubmitting] = useState(false);
  // Spec 128 — live progress across the chunked backfill loop.
  const [aioProgress, setAioProgress] = useState<{ reanalyzed: number; remaining: number } | null>(null);
  // Spec 196 — Gemini source-domain repair: same modal/confirm + progress shape.
  // Spec 198 — the run has two phases, so progress carries which one is live.
  const [geminiModalOpen, setGeminiModalOpen] = useState(false);
  const [geminiSubmitting, setGeminiSubmitting] = useState(false);
  const [geminiProgress, setGeminiProgress] = useState<GeminiSourceDomainProgress | null>(null);
  // Spec 199 - AI Overview search-status repair: same modal/confirm + progress shape.
  const [searchStatusModalOpen, setSearchStatusModalOpen] = useState(false);
  const [searchStatusSubmitting, setSearchStatusSubmitting] = useState(false);
  const [searchStatusProgress, setSearchStatusProgress] = useState<AioSearchStatusProgress | null>(null);
  // Spec 204 — source-classification repair (spec 202's three-phase route).
  const [classifyModalOpen, setClassifyModalOpen] = useState(false);
  const [classifySubmitting, setClassifySubmitting] = useState(false);
  const [classifyProgress, setClassifyProgress] = useState<DomainClassificationProgress | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<DomainListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'domains' | 'gap'>('domains');
  const [selectedDomains, setSelectedDomains] = useState<Set<string>>(new Set());
  const [bulkType, setBulkType] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [lastComputed, setLastComputed] = useState<string | null>(null);
  const [authorityLoading, setAuthorityLoading] = useState(false);
  const [trafficLoading, setTrafficLoading] = useState(false);

  const offset = parseInt(searchParams.get('offset') ?? '0', 10) || 0;
  const engineFilter = searchParams.get('engine') ?? '';
  const typeFilter = searchParams.get('type') ?? '';

  const fetchDomains = useCallback(async () => {
    if (!selectedBrand || !user) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(offset));
      if (engineFilter) params.set('engine', engineFilter);
      if (typeFilter) params.set('type', typeFilter);
      const url = `/api/v1/tenants/${selectedBrand.tenant_id}/brands/${selectedBrand.id}/sources/domains?${params.toString()}`;
      const result = await apiGet<DomainListResponse>(url);
      setData(result);
      // Spec 135: seed "Last run" from the real latest computed_at so the panel
      // doesn't show "never" when scores already exist. Authoritative over the
      // optimistic in-session value set by handleComputeScores.
      setLastComputed(result.last_computed_at ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load domains');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, user, offset, engineFilter, typeFilter]);

  useEffect(() => { fetchDomains(); }, [fetchDomains]);

  const handleComputeScores = async () => {
    if (!selectedBrand || !user) return;
    try {
      const res = await apiFetch(
        `/api/v1/tenants/${selectedBrand.tenant_id}/brands/${selectedBrand.id}/sources/scores/compute`,
        { method: 'POST' },
      );
      if (!res.ok) throw new Error('Compute failed');
      const result = await res.json() as { data: { scores_computed: number } };
      const count = result.data?.scores_computed ?? 0;
      setLastComputed(new Date().toISOString());
      addToast(`Domain scores recomputed: ${count} score${count !== 1 ? 's' : ''}`, 'success');
      fetchDomains();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Compute failed', 'error');
    }
  };

  const handleRefreshAuthority = async () => {
    if (!user || !selectedBrand) return;
    setAuthorityLoading(true);
    try {
      const res = await apiFetch(
        `/api/v1/tenants/${selectedBrand.tenant_id}/sources/authority/refresh`,
        { method: 'POST' },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'Unknown error' })) as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const result = await res.json() as { data: { updated: number; errors: number } };
      addToast(`Authority scores refreshed: ${result.data.updated} updated, ${result.data.errors} errors`, 'success');
      fetchDomains();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Authority refresh failed', 'error');
    } finally {
      setAuthorityLoading(false);
    }
  };

  const handleRefreshTraffic = async () => {
    if (!user || !selectedBrand) return;
    setTrafficLoading(true);
    try {
      const res = await apiFetch(
        `/api/v1/tenants/${selectedBrand.tenant_id}/sources/traffic/refresh`,
        { method: 'POST' },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'Unknown error' })) as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const result = await res.json() as { data: { updated: number } };
      addToast(`Traffic data refreshed: ${result.data.updated} domains updated`, 'success');
      fetchDomains();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Traffic refresh failed', 'error');
    } finally {
      setTrafficLoading(false);
    }
  };

  // Spec 127 — re-run Google AIO citation analysis for the selected brand
  // (spec 126 endpoint). Re-extracts captured google_aio responses from stored
  // aio_sources + rescores; no new API cost. Refetch so corrected numbers show.
  const handleRunAioBackfill = async () => {
    if (aioSubmitting || !selectedBrand) return;
    setAioSubmitting(true);
    // Spec 129 — do NOT seed aioProgress here. Leaving it null until the first
    // batch's onProgress fires avoids a misleading "finishing…" flash; the
    // button falls back to "Re-analyzing…" while aioProgress is null.
    try {
      // Spec 128 — drive the chunked loop; progress updates between batches.
      const result = await runAioBackfill(selectedBrand.id, (p) => setAioProgress(p));
      // Spec 205 — a brand can still hold Google answers whose search result was
      // never recorded. Those keep their old figures on purpose, so the operator
      // is told, in plain language, which control to run first and to come back.
      const unresolved = result.unresolved_search_status ?? 0;
      const unresolvedNote = unresolved > 0
        ? ` · ${unresolved} answer${unresolved !== 1 ? 's' : ''} kept existing figures (no recorded search result — run "Fix AI Overview search records" first, then run this again)`
        : '';
      addToast(
        `AIO citations re-analyzed: ${result.responses_reanalyzed} response${result.responses_reanalyzed !== 1 ? 's' : ''} · ${result.domains_rescored} domain${result.domains_rescored !== 1 ? 's' : ''} rescored${unresolvedNote}`,
        'success',
      );
      setAioModalOpen(false);
      handleComputeScores();
      fetchDomains();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'AIO citation backfill failed', 'error');
      setAioModalOpen(false);
    } finally {
      setAioSubmitting(false);
      setAioProgress(null);
    }
  };

  // Spec 196 — re-attribute Gemini sources that were all recorded against the
  // Google redirect host. Reads stored responses only (no engine API calls) and
  // recovers the publisher domain, not the specific page URL. Refetch afterwards
  // so the corrected domain rows show.
  // Spec 198 — the helper drives two phases (repair, then rescore) and resolves
  // only once BOTH report done, so resolution is what licenses a "finished" toast.
  const handleRunGeminiSourceRepair = async () => {
    if (geminiSubmitting || !selectedBrand) return;
    setGeminiSubmitting(true);
    // Leave geminiProgress null until the first batch reports, so the button
    // doesn't flash a misleading count (same reasoning as spec 129 for AIO).
    try {
      const result = await runGeminiSourceDomainRepair(selectedBrand.id, (p) => setGeminiProgress(p));
      const unidentified = result.sources_unattributed > 0
        ? ` · ${result.sources_unattributed} source${result.sources_unattributed !== 1 ? 's' : ''} could not be identified and were left as they were`
        : '';
      // A run where every response threw would otherwise report a cheerful
      // "0 sources across 0 responses" with success styling, hiding the failures
      // in the server log. Surface them, and drop the success styling.
      const failed = result.responses_failed > 0
        ? ` · ${result.responses_failed} response${result.responses_failed !== 1 ? 's' : ''} could not be processed`
        : '';
      // Spec 198 — the score update is a second phase now, so report it (and any
      // period it could not recompute) rather than letting a silent half-run read
      // as a clean finish.
      const periodsFailed = result.periods_failed > 0
        ? ` · ${result.periods_failed} period${result.periods_failed !== 1 ? 's' : ''} could not be updated`
        : '';
      addToast(
        `Finished · re-attributed ${result.sources_reattributed} source${result.sources_reattributed !== 1 ? 's' : ''} across ${result.responses_reattributed} response${result.responses_reattributed !== 1 ? 's' : ''} · scores updated for ${result.periods_rescored} period${result.periods_rescored !== 1 ? 's' : ''}${unidentified}${failed}${periodsFailed}`,
        result.responses_failed > 0 || result.periods_failed > 0 ? 'error' : 'success',
      );
      setGeminiModalOpen(false);
      fetchDomains();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Gemini source domain repair failed', 'error');
      setGeminiModalOpen(false);
    } finally {
      setGeminiSubmitting(false);
      setGeminiProgress(null);
    }
  };

  // Spec 199 - write the missing web-search record onto captured Google AI
  // Overview responses. Reads only stored capture data (no DataForSEO re-query,
  // no engine call, no cost) and only stamps a status the stored data proves;
  // anything indeterminate is left `unknown`. Refetch afterwards so corrected
  // rows show.
  // Spec 201 - correcting the records alone left the screen unchanged, because
  // the citation figures are read from the already-computed score buckets. The
  // run now has a second phase that recomputes those buckets day by day, so the
  // toast and the progress line both have to account for two halves of work.
  const handleRunAioSearchStatusRepair = async () => {
    if (searchStatusSubmitting || !selectedBrand) return;
    setSearchStatusSubmitting(true);
    // Leave progress null until the first batch reports, so the button doesn't
    // flash a misleading count (same reasoning as spec 129 for AIO citations).
    try {
      const result = await runAioSearchStatusRepair(
        selectedBrand.id,
        (p) => setSearchStatusProgress(p),
      );
      const corrected = result.updated_invoked + result.updated_ran_no_answer;
      // A run that could prove nothing would otherwise report a cheerful
      // "0 corrected" with success styling and no explanation. Name the
      // left-alone answers instead of hiding them.
      const unprovable = result.skipped_unprovable > 0
        ? ` \u00b7 ${result.skipped_unprovable} answer${result.skipped_unprovable !== 1 ? 's' : ''} could not be established and ${result.skipped_unprovable !== 1 ? 'were' : 'was'} left unchanged`
        : '';
      // Spec 201 - name the second half of the work too, and refuse to report it
      // as a win when it was not. `periods_rescored` counts days WALKED;
      // `score_rows_written` counts figures actually written. The two come apart:
      // a day whose analyses never completed aggregates to nothing, so it is
      // walked, writes nothing, and still counts as rescored. A run that walked
      // days and wrote nothing has changed nothing the user came here to fix.
      const wroteNothing = result.periods_rescored > 0 && result.score_rows_written === 0;
      const days = `${result.periods_rescored} day${result.periods_rescored !== 1 ? 's' : ''}`;
      const rescored = wroteNothing
        ? ` \u00b7 figures re-checked for ${days} \u00b7 none could be worked out from the stored answers`
        : ` \u00b7 figures updated for ${days}`;
      // A day whose recompute threw is skipped for this run - the walk advances
      // past it so one bad day cannot wedge the rest - so it has to be named.
      const daysFailed = result.periods_failed > 0
        ? ` \u00b7 ${result.periods_failed} day${result.periods_failed !== 1 ? 's' : ''} could not be updated`
        : '';
      // Only 'success' | 'error' exist (useToast.ts), and spec 198's Gemini toast
      // already drops to 'error' for a half-run that would otherwise read as a
      // clean finish. Same call here rather than inventing a third level.
      addToast(
        `Finished \u00b7 ${corrected} answer${corrected !== 1 ? 's' : ''} corrected (${result.updated_invoked} with an AI answer, ${result.updated_ran_no_answer} with none)${unprovable}${rescored}${daysFailed}`,
        result.periods_failed > 0 || wroteNothing ? 'error' : 'success',
      );
      setSearchStatusModalOpen(false);
      fetchDomains();
    } catch (err) {
      addToast(
        err instanceof Error ? err.message : 'AI Overview search-record repair failed',
        'error',
      );
      setSearchStatusModalOpen(false);
    } finally {
      setSearchStatusSubmitting(false);
      setSearchStatusProgress(null);
    }
  };

  // Spec 202 / 204 - the classifier now recognises Danish and European
  // institutions, but three layers cache the old answer (the domain's stored
  // type, each response's stored authority, and the aggregated score buckets),
  // so correcting the rules changes nothing that already exists. This drives
  // the three-phase repair across all of them. Reads only stored data - no
  // engine calls, no cost.
  const handleRunDomainClassificationRepair = async () => {
    if (classifySubmitting || !selectedBrand) return;
    setClassifySubmitting(true);
    try {
      const result = await runDomainClassificationRepair(
        selectedBrand.id,
        (p) => setClassifyProgress(p),
      );
      // Where a source carries a manually-set authority score, that score wins
      // over the classification outright - so those labels correct and their
      // figures deliberately do not move. Saying so is the difference between a
      // correct run and one that looks broken.
      const overridden = result.overridden > 0
        ? ` · ${result.overridden} kept ${result.overridden !== 1 ? 'their' : 'its'} manually-set score, so ${result.overridden !== 1 ? 'those figures' : 'that figure'} did not move`
        : '';
      // Same honesty rule as the AI Overview repair: days WALKED and figures
      // WRITTEN come apart, and a run that walked days and wrote nothing has not
      // changed what the user came here to fix.
      const wroteNothing = result.periods_rescored > 0 && result.score_rows_written === 0;
      const days = `${result.periods_rescored} day${result.periods_rescored !== 1 ? 's' : ''}`;
      const rescored = wroteNothing
        ? ` · figures re-checked for ${days} · none could be worked out from the stored answers`
        : ` · figures updated for ${days}`;
      const failures = result.responses_failed > 0 || result.periods_failed > 0
        ? ` · ${result.responses_failed} answer${result.responses_failed !== 1 ? 's' : ''} and ${result.periods_failed} day${result.periods_failed !== 1 ? 's' : ''} could not be updated`
        : '';
      addToast(
        `Finished · ${result.reclassified} source${result.reclassified !== 1 ? 's' : ''} re-labelled · ${result.responses_reanalyzed} answer${result.responses_reanalyzed !== 1 ? 's' : ''} re-scored${overridden}${rescored}${failures}`,
        result.responses_failed > 0 || result.periods_failed > 0 || wroteNothing ? 'error' : 'success',
      );
      setClassifyModalOpen(false);
      fetchDomains();
    } catch (err) {
      addToast(
        err instanceof Error ? err.message : 'Source classification repair failed',
        'error',
      );
      setClassifyModalOpen(false);
    } finally {
      setClassifySubmitting(false);
      setClassifyProgress(null);
    }
  };

  const handlePageChange = useCallback((newOffset: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newOffset > 0) next.set('offset', String(newOffset));
      else next.delete('offset');
      return next;
    });
  }, [setSearchParams]);

  const handleFilterChange = useCallback((key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      next.delete('offset');
      return next;
    });
  }, [setSearchParams]);

  if (!selectedBrand) {
    return (
      <div className="hub-page--table" data-density="compact">
        <h1 className="hub-page__title">Domains</h1>
        <EmptyState icon="bx-globe" title="Select a brand" description="" />
      </div>
    );
  }

  return (
    <div data-testid="sources-page" className="hub-page--table" data-density="compact">
      <div className="hub-page__head">
        <div>
          <h1 className="hub-page__title">Domains</h1>
          <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">Domain intelligence for {selectedBrand.name}</p>
          {/* Spec 256: the loop as navigation — this page's stage inverted, the next one click away. */}
          <div className="mt-3">
            <StageLoop current="analyze" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          {data && <p className="text-sm text-[var(--hub-color-ink-muted)]">{data.total} domain{data.total !== 1 ? 's' : ''}</p>}
          {/* Spec 249 (review P1-2): six maintenance actions used to wrap onto
              two rows above the content. They are operator tools, run now and
              then, so they sit behind one round button at the end of the head.
              Each keeps its testid and its in-progress label. */}
          <OverflowMenu
            label="Maintenance"
            testId="domains-maintenance"
            items={[
              {
                label: authorityLoading ? 'Refreshing authority scores…' : 'Refresh authority scores',
                icon: 'bx-refresh',
                onSelect: handleRefreshAuthority,
                disabled: authorityLoading,
                testId: 'refresh-authority-btn',
              },
              {
                label: trafficLoading ? 'Refreshing traffic data…' : 'Refresh traffic data',
                icon: 'bx-line-chart',
                onSelect: handleRefreshTraffic,
                disabled: trafficLoading,
                testId: 'refresh-traffic-btn',
              },
              canRunSourceRepairs && {
                label: aioSubmitting
                  ? aioProgress
                    ? `Re-analyzed ${aioProgress.reanalyzed}…`
                    : 'Re-analyzing…'
                  : 'Re-run AIO citations',
                icon: 'bx-revision',
                onSelect: () => setAioModalOpen(true),
                disabled: aioSubmitting,
                testId: 'aio-backfill-button',
              },
              canRunSourceRepairs && {
                label: geminiSubmitting
                  ? geminiProgress
                    ? `Re-attributing ${geminiProgress.reattributed}…`
                    : 'Re-attributing…'
                  : 'Fix Gemini source domains',
                icon: 'bx-wrench',
                onSelect: () => setGeminiModalOpen(true),
                disabled: geminiSubmitting,
                testId: 'gemini-source-domain-repair',
              },
              canRunSourceRepairs && {
                label: searchStatusSubmitting
                  ? searchStatusProgress
                    ? searchStatusProgress.phase === 'rescore'
                      ? 'Updating figures…'
                      : `Correcting ${searchStatusProgress.updated}…`
                    : 'Correcting…'
                  : 'Fix AI Overview search records',
                icon: 'bx-wrench',
                onSelect: () => setSearchStatusModalOpen(true),
                disabled: searchStatusSubmitting,
                testId: 'aio-search-status-repair',
              },
              canRunSourceRepairs && {
                label: classifySubmitting
                  ? classifyProgress
                    ? `Part ${classifyProgress.phaseIndex} of 3…`
                    : 'Re-labelling…'
                  : 'Fix source labels',
                icon: 'bx-purchase-tag',
                onSelect: () => setClassifyModalOpen(true),
                disabled: classifySubmitting,
                testId: 'domain-classification-repair',
              },
            ]}
          />
        </div>
      </div>

      <ProcessingStatus
        label="Domain scores"
        lastRun={lastComputed}
        count={data?.total ?? null}
        countLabel="domains"
        status={data && data.total > 0 ? 'complete' : 'never'}
        onRun={handleComputeScores}
        runLabel="Recompute"
      />

      <TypeDistributionChart />

      {/* Source Trend Chart (Spec 044) */}
      <section data-testid="source-trend-section">
        <h2 className="text-base font-semibold text-[var(--hub-color-ink)]">Source retrieval trends</h2>
        <div className="mt-2">
          <SourceTrendChart engine={engineFilter || undefined} />
        </div>
      </section>

      {/* Spec 249 (review P1-11): "Domains | Gap Analysis" switches CONTENT, so
          it is tabs, underlined — segmented is for parameters. The testids the
          toggle's buttons carried move onto the tabs. */}
      <Tabs
        tabs={DOMAIN_VIEWS}
        activeTab={viewMode}
        onTabChange={setViewMode}
        ariaLabel="Domains view"
      />
      <div
        role="tabpanel"
        id={tabPanelId('Domains view', viewMode)}
        aria-labelledby={`domains-view-tab-${viewMode}`}
        className="flex flex-col gap-[inherit]"
      >
      {/* Filters sit directly above the rows they filter. */}
      <div className="hub-toolbar">
        <div className="hub-field">
        <label className="hub-field__label" htmlFor="domains-engine">Engine</label>
        <Select
          id="domains-engine"
          options={[
            { value: '', label: 'All engines' },
            ...ENGINE_OPTIONS.map((e) => ({
              value: e,
              label: e === 'google_aio' ? 'Google AIO' : e.charAt(0).toUpperCase() + e.slice(1),
            })),
          ]}
          value={engineFilter}
          onChange={(v) => handleFilterChange('engine', v)}
          placeholder="All engines"
          className="w-44"
          data-testid="engine-filter"
        />
        </div>
        <div className="hub-field">
        <label className="hub-field__label" htmlFor="domains-type">Type</label>
        <Select
          id="domains-type"
          options={[
            { value: '', label: 'All types' },
            ...FILTER_TYPE_OPTIONS.map((t) => ({
              value: t,
              label: t.charAt(0).toUpperCase() + t.slice(1),
            })),
          ]}
          value={typeFilter}
          onChange={(v) => handleFilterChange('type', v)}
          placeholder="All types"
          className="w-44"
          data-testid="type-filter"
        />
        </div>
      </div>

      {error && <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {loading && <TableSkeleton rows={8} cols={6} />}

      {!loading && data && data.total === 0 && viewMode === 'domains' && (
        <div className="flex flex-col items-center justify-center border border-blue-200 bg-blue-50/50 py-16" data-testid="empty-domains">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
            <Icon name="bx-globe" className="text-blue-600" />
          </div>
          <p className="mt-3 text-base font-medium text-blue-800">No domains found yet</p>
          <p className="mt-1 text-sm text-blue-600/80">Domain scores will appear after responses are analyzed.</p>
          <a href="/responses" className="hub-btn hub-btn--primary mt-4">
            Go to responses
          </a>
        </div>
      )}

      {/* Bulk Reclassification Bar (Spec 055). Not sticky: spec 232 spends
          sticky on exactly the bar and a thead, and this page already has a
          sticky thead below. */}
      {selectedDomains.size > 0 && (
        <div className="flex items-center gap-3 rounded-md border border-[var(--hub-color-line)] bg-[var(--hub-color-action-soft)] px-4 py-3" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">{selectedDomains.size} selected</span>
          <Select
            options={SETTABLE_TYPE_OPTIONS.map((t) => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))}
            value={bulkType}
            onChange={(v) => setBulkType(v)}
            placeholder="Reclassify as..."
            className="w-44"
          />
          <Button
            variant="primary"
            disabled={!bulkType || bulkLoading}
            onClick={async () => {
              if (!bulkType || !user || !selectedBrand) return;
              setBulkLoading(true);
              try {
                await apiFetch(`/api/v1/tenants/${selectedBrand.tenant_id}/sources/domains/bulk`, {
                  method: 'PATCH',
                  body: JSON.stringify({ domain_ids: Array.from(selectedDomains), type: bulkType }),
                });
                setSelectedDomains(new Set());
                setBulkType('');
                fetchDomains();
              } catch { /* ignore */ }
              finally { setBulkLoading(false); }
            }}
          >
            {bulkLoading ? 'Updating...' : 'Apply'}
          </Button>
          <button onClick={() => { setSelectedDomains(new Set()); setBulkType(''); }}
            className="text-sm text-[var(--hub-color-action-text)]">Deselect all</button>
        </div>
      )}

      {/* Gap Analysis View (Spec 046) */}
      {!loading && data && viewMode === 'gap' && (() => {
        const brandDomains = data.data.filter((d) => d.type === 'you');
        const compDomains = data.data.filter((d) => d.type === 'competitor');
        const gaps = brandDomains.flatMap((bd) =>
          compDomains.map((cd) => {
            const brandRate = bd.retrieval_rate ?? 0;
            const compRate = cd.retrieval_rate ?? 0;
            const ratio = brandRate > 0 ? compRate / brandRate : compRate > 0 ? 999 : 1;
            return { brand: bd, competitor: cd, ratio: Math.round(ratio * 10) / 10 };
          })
        ).filter((g) => g.ratio > 1).sort((a, b) => b.ratio - a.ratio);

        if (gaps.length === 0) {
          return (
            <EmptyState title="No domain comparison data -- both brand and competitor domains must have score data" description="" testId="gap-empty" />
          );
        }

        return (
          <div className="overflow-x-auto" data-testid="gap-analysis-table">
            <table className="hub-table">
              <thead>
                <tr>
                  <th>Your domain</th>
                  <th className="is-num">Your retrieval</th>
                  <th>Competitor</th>
                  <th className="is-num">Comp. retrieval</th>
                  <th className="is-num">Gap</th>
                  <th className="text-center">Severity</th>
                </tr>
              </thead>
              <tbody>
                {gaps.slice(0, 20).map((g, i) => {
                  const severity = g.ratio > 3 ? 'critical' : g.ratio > 2 ? 'high' : g.ratio > 1.5 ? 'medium' : 'low';
                  const chipVariant: Record<string, string> = { critical: 'hub-chip--fail', high: 'hub-chip--warn', medium: 'hub-chip--info', low: 'hub-chip--ok' };
                  return (
                    <tr key={`${g.brand.domain_id}-${g.competitor.domain_id}-${i}`}>
                      <td>{g.brand.display_name ?? g.brand.domain}</td>
                      <td className="is-num">{formatRate(g.brand.retrieval_rate)}</td>
                      <td>{g.competitor.display_name ?? g.competitor.domain}</td>
                      <td className="is-num">{formatRate(g.competitor.retrieval_rate)}</td>
                      <td className="is-num">{g.ratio}x</td>
                      <td className="text-center">
                        <span className={`hub-chip ${chipVariant[severity]}`}>{severity}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}

      {!loading && data && data.data.length > 0 && viewMode === 'domains' && (
        <>
          <div className="overflow-x-auto">
            <table className="hub-table" data-testid="domain-table">
              <thead>
                <tr>
                  <th className="w-8">
                    <input type="checkbox"
                      checked={data!.data.length > 0 && data!.data.every((d) => selectedDomains.has(d.domain_id))}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedDomains(new Set(data!.data.map((d) => d.domain_id)));
                        else setSelectedDomains(new Set());
                      }}
                      data-testid="select-all-domains"
                    />
                  </th>
                  <th>Domain</th>
                  <th>Type</th>
                  <th className="is-num">Retrieval</th>
                  <th className="text-center">Trend</th>
                  <th className="is-num">Citation</th>
                  <th className="text-center">Trend</th>
                  <th className="is-num">Traffic</th>
                  <th className="is-num">Samples</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((item: DomainListItem) => (
                  <tr key={item.domain_id} className="cursor-pointer"
                    data-testid={`domain-row-${item.domain_id}`}>
                    <td className="w-8" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox"
                        checked={selectedDomains.has(item.domain_id)}
                        onChange={() => {
                          setSelectedDomains((prev) => {
                            const next = new Set(prev);
                            if (next.has(item.domain_id)) next.delete(item.domain_id);
                            else next.add(item.domain_id);
                            return next;
                          });
                        }}
                      />
                    </td>
                    <td onClick={() => navigate(`/sources/${item.domain_id}`)}>
                      <div className="font-medium">{item.display_name ?? item.domain}</div>
                      <div className="text-xs text-[var(--hub-color-ink-muted)]">{item.domain}</div>
                    </td>
                    <td><DomainTypeBadge type={item.type} /></td>
                    <td className="is-num">{formatRate(item.retrieval_rate)}</td>
                    <td className="text-center"><TrendArrow value={item.trend_retrieval} /></td>
                    <td className="is-num">{formatRate(item.citation_rate)}</td>
                    <td className="text-center"><TrendArrow value={item.trend_citation} /></td>
                    <td className="is-num">
                      {item.monthly_traffic != null ? item.monthly_traffic.toLocaleString() : <span className="text-[var(--hub-color-ink-subtle)]">--</span>}
                    </td>
                    <td className="is-num"><ReliabilityLabel sampleSize={item.sample_size} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination total={data.total} limit={PAGE_SIZE} offset={offset} onPageChange={handlePageChange} />
        </>
      )}

      </div>

      {/* Spec 127 — AIO citation backfill confirmation modal (admin-only). */}
      {aioModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="aio-backfill-modal-title"
          data-testid="aio-backfill-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
          <div className="w-full max-w-md rounded-lg bg-white">
            <div className="border-b border-slate-200 px-5 py-3">
              <h2
                id="aio-backfill-modal-title"
                data-testid="aio-backfill-modal-title"
                className="text-base font-semibold text-slate-900"
              >
                Re-run AIO citations for {selectedBrand.name}
              </h2>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm text-slate-600">
              <p>
                Re-analyzes this brand's captured <span className="font-semibold text-slate-900">Google AIO</span>{' '}
                responses using already-stored data and recomputes domain scores, so citation counts
                correctly credit the sources referenced in AI Overviews.
              </p>
              <p data-testid="aio-backfill-no-overview-note">
                Questions where Google showed no AI Overview no longer count as AI Overview
                citations, so these figures may go down. That is the correction, not lost data.
                Answers with no recorded search result are left as they are and keep their existing
                figures — if the result says any were left, run{' '}
                <span className="font-semibold text-slate-900">Fix AI Overview search records</span>{' '}
                first and then run this again.
              </p>
              <p>
                No new collection or API cost — it reads existing responses. Safe to run repeatedly.
                Runs in batches, so large brands won't time out.
              </p>
              {aioSubmitting && aioProgress && (
                <p
                  data-testid="aio-backfill-progress"
                  className="rounded-md bg-violet-50 px-3 py-2 text-violet-800"
                >
                  Re-analyzed <span className="font-semibold">{aioProgress.reanalyzed}</span>
                  {aioProgress.remaining > 0 ? ` · ${aioProgress.remaining} remaining` : ' · finishing…'}
                </p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <button
                type="button"
                data-testid="aio-backfill-cancel"
                onClick={() => { if (!aioSubmitting) setAioModalOpen(false); }}
                disabled={aioSubmitting}
                className="hub-btn hub-btn--ghost"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="aio-backfill-confirm"
                onClick={handleRunAioBackfill}
                disabled={aioSubmitting}
                className="hub-btn hub-btn--primary"
              >
                {aioSubmitting ? (
                  <span data-testid="aio-backfill-spinner">Re-analyzing…</span>
                ) : (
                  'Run backfill'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spec 196 — Gemini source-domain repair confirmation modal (admin-only). */}
      {geminiModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="gemini-source-domain-repair-modal-title"
          data-testid="gemini-source-domain-repair-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
          <div className="flex max-h-[90vh] w-full max-w-md flex-col rounded-lg bg-white">
            <div className="shrink-0 border-b border-slate-200 px-5 py-3">
              <h2
                id="gemini-source-domain-repair-modal-title"
                data-testid="gemini-source-domain-repair-modal-title"
                className="text-base font-semibold text-slate-900"
              >
                Fix Gemini source domains
              </h2>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 text-sm text-slate-600">
              <p>
                Gemini returns its sources behind a Google redirect link, so GEO Observatory has been
                recording every Gemini source as coming from the same place. This re-reads the
                responses already stored for{' '}
                <span className="font-semibold text-slate-900">{selectedBrand.name}</span> and
                attributes each source to the site that was actually cited.
              </p>
              <p>
                It uses only data GEO Observatory already has — no new API calls and no extra cost. The
                original link is kept, but the specific page behind it can't be recovered. Safe to
                run repeatedly. GEO Observatory works through the responses in small steps and then updates
                this brand's scores, so a brand with a long history can take a few minutes — keep
                this page open until it says it has finished. If it stops early nothing is damaged:
                run it again and it carries on.
              </p>
              <p>
                Older responses, captured before GEO Observatory recorded whether the engine actually ran a
                web search, will also have their &ldquo;retrieved&rdquo; markers corrected to
                &ldquo;cited&rdquo; — GEO Observatory won't claim a fetch it has no record of. That can
                move retrieval rates for those earlier dates.
              </p>
              {geminiSubmitting && geminiProgress && (
                <p
                  data-testid="gemini-source-domain-repair-progress"
                  className="rounded-md bg-violet-50 px-3 py-2 text-violet-800"
                >
                  {geminiProgress.phase === 'repair' ? (
                    <>
                      Re-attributing… <span className="font-semibold">{geminiProgress.reattributed}</span>{' '}
                      responses{geminiProgress.remaining > 0 ? ` (${geminiProgress.remaining} left)` : ' (finishing…)'}
                    </>
                  ) : (
                    <>
                      Updating scores…{' '}
                      {geminiProgress.periodsRemaining > 0
                        ? `(${geminiProgress.periodsRemaining} period${geminiProgress.periodsRemaining !== 1 ? 's' : ''} left)`
                        : '(finishing…)'}
                    </>
                  )}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <button
                type="button"
                data-testid="gemini-source-domain-repair-cancel"
                onClick={() => { if (!geminiSubmitting) setGeminiModalOpen(false); }}
                disabled={geminiSubmitting}
                className="hub-btn hub-btn--ghost"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="gemini-source-domain-repair-confirm"
                onClick={handleRunGeminiSourceRepair}
                disabled={geminiSubmitting}
                className="hub-btn hub-btn--primary"
              >
                {geminiSubmitting ? (
                  <span data-testid="gemini-source-domain-repair-spinner">
                    {geminiProgress?.phase === 'rescore' ? 'Updating scores…' : 'Re-attributing…'}
                  </span>
                ) : (
                  'Fix source domains'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spec 199 - AI Overview search-record repair confirmation modal (admin-only). */}
      {searchStatusModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="aio-search-status-repair-modal-title"
          data-testid="aio-search-status-repair-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
          <div className="flex max-h-[90vh] w-full max-w-md flex-col rounded-lg bg-white">
            <div className="shrink-0 border-b border-slate-200 px-5 py-3">
              <h2
                id="aio-search-status-repair-modal-title"
                data-testid="aio-search-status-repair-modal-title"
                className="text-base font-semibold text-slate-900"
              >
                Fix AI Overview search records
              </h2>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 text-sm text-slate-600">
              <p>
                For every answer it captures, GEO Observatory records whether the engine actually ran a web
                search. Google&rsquo;s{' '}
                <span className="font-semibold text-slate-900">AI Overview</span> answers were
                captured without that record, so they read as &ldquo;status unknown&rdquo;. This
                re-reads what GEO Observatory already stored for{' '}
                <span className="font-semibold text-slate-900">{selectedBrand.name}</span> and writes
                the missing record: either Google returned an AI Overview, or the search ran and
                produced no AI answer.
              </p>
              <p>
                This runs in two parts. First it corrects the search records, using only data GEO Observatory
                already has — no new collection calls and no cost. Then it updates the visibility
                figures that read those records, working back through the days this brand has been
                collected so the history is corrected too, not just today.
              </p>
              <p>
                Only answers whose stored capture data proves which of the two cases they were get
                corrected. Where that can&rsquo;t be established, the answer is left exactly as it
                is rather than guessed at, and it stays out of the citation figures.
              </p>
              <p>
                It works through both parts in small steps and is safe to run again: nothing is
                corrected twice, and a run that stops early picks up where it left off.
              </p>
              {searchStatusSubmitting && searchStatusProgress && (
                <p
                  data-testid="aio-search-status-repair-progress"
                  className="rounded-md bg-violet-50 px-3 py-2 text-violet-800"
                >
                  {searchStatusProgress.phase === 'repair' ? (
                    <>
                      part 1 of 2 · Corrected{' '}
                      <span className="font-semibold">{searchStatusProgress.updated}</span> of{' '}
                      {searchStatusProgress.scanned} checked
                      {searchStatusProgress.remaining > 0
                        ? ` (${searchStatusProgress.remaining} left)`
                        : ' (finishing…)'}
                    </>
                  ) : (
                    <>
                      part 2 of 2 · Updating figures ·{' '}
                      <span className="font-semibold">{searchStatusProgress.periodsRescored}</span>{' '}
                      day{searchStatusProgress.periodsRescored !== 1 ? 's' : ''} updated
                      {searchStatusProgress.periodsRemaining > 0
                        ? ` (${searchStatusProgress.periodsRemaining} left)`
                        : ' (finishing…)'}
                    </>
                  )}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <button
                type="button"
                data-testid="aio-search-status-repair-cancel"
                onClick={() => { if (!searchStatusSubmitting) setSearchStatusModalOpen(false); }}
                disabled={searchStatusSubmitting}
                className="hub-btn hub-btn--ghost"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="aio-search-status-repair-confirm"
                onClick={handleRunAioSearchStatusRepair}
                disabled={searchStatusSubmitting}
                className="hub-btn hub-btn--primary"
              >
                {searchStatusSubmitting ? (
                  <span data-testid="aio-search-status-repair-spinner">
                    {searchStatusProgress?.phase === 'rescore'
                      ? 'Updating figures…'
                      : searchStatusProgress
                        ? `Correcting ${searchStatusProgress.updated}…`
                        : 'Correcting…'}
                  </span>
                ) : (
                  'Fix search records'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spec 204 — source-classification repair confirmation (admin-only).
          Height-capped flex column: an un-capped panel runs its header and
          action row off-screen on a short viewport, a defect this codebase has
          shipped more than once. */}
      {classifyModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="domain-classification-repair-modal-title"
          data-testid="domain-classification-repair-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
          <div className="flex max-h-[90vh] w-full max-w-md flex-col rounded-lg bg-white">
            <div className="shrink-0 border-b border-slate-200 px-5 py-3">
              <h2
                id="domain-classification-repair-modal-title"
                data-testid="domain-classification-repair-modal-title"
                className="text-base font-semibold text-slate-900"
              >
                Fix source labels
              </h2>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 text-sm text-slate-600">
              <p>
                GEO Observatory now recognises Danish and European public authorities, national
                media and reference works, which it previously filed alongside anonymous
                websites. Sources already stored kept the old label, so this re-reads them
                and applies the corrected one.
              </p>
              <p data-testid="domain-classification-repair-corporate-note">
                That now includes sources filed as company websites, which until recently
                could not be re-read at all. Where GEO Observatory recognises one as a public
                authority, a news outlet or a reference work, its label is corrected.
                Anything it does not recognise keeps the label it has.
              </p>
              <p>
                It runs in three parts: re-labelling the sources, re-scoring the answers
                that cite them, then updating the figures those answers feed — working back
                through this brand&rsquo;s history so earlier dates are corrected too, not
                just today. It uses only data GEO Observatory already has — no new collection and no
                cost.
              </p>
              <p>
                Where someone has set a source&rsquo;s authority score by hand, that score
                still wins, so those labels change while their figures stay where they are.
                The result says how many were affected.
              </p>
              <p>
                Safe to run again: nothing is corrected twice, and a run that stops early
                picks up where it left off. A brand with a long history can take a few
                minutes — keep this page open until it says it has finished.
              </p>
              {classifySubmitting && classifyProgress && (
                <p
                  data-testid="domain-classification-repair-progress"
                  className="rounded-md bg-violet-50 px-3 py-2 text-violet-800"
                >
                  {classifyProgress.phase === 'reclassify' ? (
                    <>
                      part 1 of 3 · Re-labelled{' '}
                      <span className="font-semibold">{classifyProgress.reclassified}</span> source
                      {classifyProgress.reclassified !== 1 ? 's' : ''}
                      {classifyProgress.remaining > 0 ? ` (${classifyProgress.remaining} left)` : ' (finishing…)'}
                    </>
                  ) : classifyProgress.phase === 'reanalyze' ? (
                    <>
                      part 2 of 3 · Re-scored{' '}
                      <span className="font-semibold">{classifyProgress.responsesReanalyzed}</span> answer
                      {classifyProgress.responsesReanalyzed !== 1 ? 's' : ''}
                      {classifyProgress.remaining > 0 ? ` (${classifyProgress.remaining} left)` : ' (finishing…)'}
                    </>
                  ) : (
                    <>
                      part 3 of 3 · Updating figures ·{' '}
                      <span className="font-semibold">{classifyProgress.periodsRescored}</span> day
                      {classifyProgress.periodsRescored !== 1 ? 's' : ''} updated
                      {classifyProgress.remaining > 0 ? ` (${classifyProgress.remaining} left)` : ' (finishing…)'}
                    </>
                  )}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
              <button
                type="button"
                data-testid="domain-classification-repair-cancel"
                onClick={() => { if (!classifySubmitting) setClassifyModalOpen(false); }}
                disabled={classifySubmitting}
                className="hub-btn hub-btn--ghost"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="domain-classification-repair-confirm"
                onClick={handleRunDomainClassificationRepair}
                disabled={classifySubmitting}
                className="hub-btn hub-btn--primary"
              >
                {classifySubmitting ? (
                  <span data-testid="domain-classification-repair-spinner">
                    {classifyProgress ? `Part ${classifyProgress.phaseIndex} of 3…` : 'Re-labelling…'}
                  </span>
                ) : (
                  'Fix source labels'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
